import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { UserRoles, IGetRestaurantsShort, IGetRestaurantsShortArgs, EntityStatus } from 'types';
import { getRestaurantsShortInfo, copyMenuV2 } from 'backend/restaurants';
import { ModalContent } from 'components/ModalWindows/ModalContent';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { RestaurantsMultipleSelect } from './RestaurantsMultipleSelect';
import style from './index.module.scss';

interface IProps {
	onCloseModal: () => void;
	onFailureEvent?: () => void;
	onShowError: () => void;
	customer?: { id: string; name: string };
}

export const ModalCopyMenu: React.FC<IProps> = ({ onCloseModal, onFailureEvent, onShowError, customer }) => {
	const { showLoader, hideLoader, loggedUser } = useContext<IAppContext>(AppContext);
	const [selectedRestaurantFrom, setSelectedRestaurantFrom] = useState<Nullable<string>>(null);
	const [selectedRestaurantsTo, setSelectedRestaurantsTo] = useState<string[]>([]);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		customer?.id && hasAccess ? getRestaurantsShortInfo : null,
		{
			customerId: customer?.id || '',
			menuApiVersion: 'v2',
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const restaurants = useMemo(() => {
		return hasAccess ? response?.data.restaurants : loggedUser.restaurants;
	}, [hasAccess, response, loggedUser]);

	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);

	const restaurantFromOptions = useMemo(() => {
		const items = restaurants || [];
		const visibleItems = showArchived
			? items
			: items.filter((el) => el.status === EntityStatus.Active || el.id === selectedRestaurantFrom);

		return visibleItems.map((el) => ({
			...el,
			additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [restaurants, showArchived, selectedRestaurantFrom]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	const handleCopyMenu = async () => {
		if (selectedRestaurantFrom && selectedRestaurantsTo.length) {
			try {
				showLoader();
				await copyMenuV2({
					fromRestaurantId: selectedRestaurantFrom,
					toRestaurantIds: selectedRestaurantsTo,
				});
				onCloseModal();
			} catch (err) {
				onShowError();
			} finally {
				hideLoader();
			}
		}
	};

	const handleSetSelectedRestaurantFrom = (value: Nullable<string>) => {
		setSelectedRestaurantFrom(value);
		const filteredSelectedRestaurantsTo = selectedRestaurantsTo.filter(
			(selectedRestaurant) => selectedRestaurant !== value,
		);
		setSelectedRestaurantsTo(filteredSelectedRestaurantsTo);
	};

	return (
		<ModalContent
			onConsentEvent={handleCopyMenu}
			onFailureEvent={onFailureEvent ? onFailureEvent : onCloseModal}
			heading="copyMenuTitle"
			headerTranslationNameSpace="form"
			translationOfConfirmBtn="saveButton"
			headingAlignment="left"
			centered={false}
			width="60%"
			isDisabledSave={!selectedRestaurantFrom || !selectedRestaurantsTo.length}>
			<div className={style.textFieldsWrapper}>
				<FullInfoField label="customerLabel" />
				<Text translationKey={customer?.name} size="s" />
			</div>
			<div className={style.textFieldsWrapper}>
				<Text translationNameSpace="modals" translationKey="step1" theme="secondary" size="s" weight="bold" />
				<Text
					translationNameSpace="modals"
					translationKey="selectRestaurantFromWhichYouWantToCopyMenu"
					size="s"
				/>
			</div>
			<OutsideFormSelect
				testId="RestaurantSelect"
				name="IDSelectedRestaurant"
				label="restaurantLabel"
				onChange={handleSetSelectedRestaurantFrom}
				value={selectedRestaurantFrom}
				options={restaurantFromOptions}
				keepSearchWithDropdownRender={true}
				dropdownRender={renderSelectMenu}
			/>
			<div className={style.textFieldsWrapper}>
				<Text translationNameSpace="modals" translationKey="step2" theme="secondary" size="s" weight="bold" />
				<Text
					translationNameSpace="modals"
					translationKey="selectRestaurantOrRestaurantsWhereYouWantToCopyMenu"
					size="s"
				/>
			</div>
			<RestaurantsMultipleSelect
				setSelectedRestaurantsTo={setSelectedRestaurantsTo}
				restaurants={restaurants || []}
				IDSelectedRestaurantFrom={selectedRestaurantFrom}
				selectedRestaurantsTo={selectedRestaurantsTo}
			/>
		</ModalContent>
	);
};
